import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Save, FileText, Image, CodeXml, FileType, Globe, Minus, Plus, LayoutGrid, Pencil, Trash2, Check, X } from "lucide-react";
import { useDroppable } from "@dnd-kit/core";
import { useI18n } from "@/lib/i18n";
import { useAppData } from "@/hooks/useAppData";
import {
  addHistoryItem,
  addWidgetPage,
  DEFAULT_WIDGET_PAGE_COLUMNS,
  DEFAULT_WIDGET_PAGE_ROWS,
  updateDataItem,
  updateWidgetPage,
} from "@/lib/db";
import { emitDataChanged } from "@/hooks/useAppData";
import { useClipboard } from "@/hooks/useClipboard";
import { emitPreviewRequest } from "@/events/previewBus";
import { useGlobalSelection } from "@/hooks/useGlobalSelection";
import { useWidgetPageState } from "@/hooks/useWidgetPageState";
import { deleteSelectedItems, deleteWidgetPageWithWidgets } from "@/lib/deleteActions";
import DeleteConfirmDialog from "@/components/shared/DeleteConfirmDialog";
import ConfirmDialog from "@/components/shared/ConfirmDialog";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isHtml, isMarkdown, isJson, isUrl } from "@/lib/textUtils";
import type { ClipboardPreview, DataItemType, HistoryItem, WidgetPage } from "@/types";

import { WidgetCard } from "@/components/areas/WidgetCard";

type WidgetFilter = DataItemType | "html" | "markdown" | "all";
type GridField = "columns" | "rows";
type PageDropPosition = "before" | "after";
type PageEdgeDropPosition = "start" | "end";
type PageDropIndicator =
  | { type: "page"; pageId: string; position: PageDropPosition }
  | { type: "edge"; position: PageEdgeDropPosition };

const COLUMN_MIN = 1;
const COLUMN_MAX = 8;
const ROW_MIN = 1;
const ROW_MAX = 6;
const GRID_STEP = 0.5;
const GRID_GAP = 8;
const CARD_CHROME_HEIGHT = 48;
const MIN_WIDGET_CONTENT_HEIGHT = 48;

function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function normalizeGridValue(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Number(clampNumber(value, min, max).toFixed(2));
}

function formatGridValue(value: number) {
  return Number(value.toFixed(2)).toString();
}

function parseGridValue(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return null;

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function getGridColumnSize(columns: number) {
  const formattedColumns = formatGridValue(columns);
  return `calc((100% - ${GRID_GAP}px * (${formattedColumns} - 1)) / ${formattedColumns})`;
}

function getPageDropPosition(event: React.DragEvent<HTMLElement>): PageDropPosition {
  const rect = event.currentTarget.getBoundingClientRect();
  return event.clientX > rect.left + rect.width / 2 ? "after" : "before";
}

function insertWidgetPageAtIndex(
  pages: WidgetPage[],
  draggedPageId: string,
  insertIndex: number,
) {
  const draggedPage = pages.find((page) => page.id === draggedPageId);
  if (!draggedPage) return pages;

  const remainingPages = pages.filter((page) => page.id !== draggedPageId);
  const boundedIndex = Math.min(Math.max(insertIndex, 0), remainingPages.length);
  return [
    ...remainingPages.slice(0, boundedIndex),
    draggedPage,
    ...remainingPages.slice(boundedIndex),
  ];
}

function reorderWidgetPages(
  pages: WidgetPage[],
  draggedPageId: string,
  targetPageId: string,
  position: PageDropPosition,
) {
  if (draggedPageId === targetPageId) return pages;

  const remainingPages = pages.filter((page) => page.id !== draggedPageId);
  const targetIndex = remainingPages.findIndex((page) => page.id === targetPageId);
  if (targetIndex === -1) return pages;

  const insertIndex = position === "after" ? targetIndex + 1 : targetIndex;
  return insertWidgetPageAtIndex(pages, draggedPageId, insertIndex);
}

function reorderWidgetPagesToEdge(
  pages: WidgetPage[],
  draggedPageId: string,
  position: PageEdgeDropPosition,
) {
  return insertWidgetPageAtIndex(
    pages,
    draggedPageId,
    position === "start" ? 0 : pages.length - 1,
  );
}

interface GridStepperProps {
  label: string;
  value: number;
  min: number;
  max: number;
  testId: string;
  disabled?: boolean;
  onStep: (delta: number) => void;
  onCommit: (value: number) => void;
}

function GridStepper({ label, value, min, max, testId, disabled, onStep, onCommit }: GridStepperProps) {
  const [draftValue, setDraftValue] = useState(formatGridValue(value));

  useEffect(() => {
    setDraftValue(formatGridValue(value));
  }, [value]);

  const commitDraftValue = useCallback(() => {
    const parsedValue = parseGridValue(draftValue);

    if (parsedValue === null) {
      setDraftValue(formatGridValue(value));
      return;
    }

    const nextValue = normalizeGridValue(parsedValue, min, max);
    setDraftValue(formatGridValue(nextValue));

    if (nextValue !== value) {
      onCommit(nextValue);
    }
  }, [draftValue, max, min, onCommit, value]);

  return (
    <div className="flex items-center gap-1.5 rounded-md border border-border/40 bg-background/30 px-1.5 py-1">
      <span className="text-[10px] font-medium text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={`${label} -`}
          data-testid={`${testId}-decrease`}
          title={`${label} -`}
          disabled={disabled || value <= min}
          onClick={() => onStep(-GRID_STEP)}
          className="flex h-5 w-5 items-center justify-center rounded bg-background/60 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
        >
          <Minus className="h-3 w-3" />
        </button>
        <input
          type="text"
          aria-label={label}
          data-testid={`${testId}-input`}
          inputMode="decimal"
          value={draftValue}
          disabled={disabled}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={commitDraftValue}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }

            if (event.key === "Escape") {
              setDraftValue(formatGridValue(value));
              event.currentTarget.blur();
            }
          }}
          className="h-5 w-11 rounded border border-border/40 bg-background/60 px-1 text-center text-[11px] tabular-nums text-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
        />
        <button
          type="button"
          aria-label={`${label} +`}
          data-testid={`${testId}-increase`}
          title={`${label} +`}
          disabled={disabled || value >= max}
          onClick={() => onStep(GRID_STEP)}
          className="flex h-5 w-5 items-center justify-center rounded bg-background/60 text-muted-foreground transition-colors hover:text-foreground disabled:opacity-30"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

const WidgetArea = () => {
  const { t } = useI18n();
  const { widgets, getDataById, loading } = useAppData();
  const { widgetPages, currentPageId, currentPage, setCurrentPageId } = useWidgetPageState();
  const { copyToClipboard } = useClipboard();
  const globalSelection = useGlobalSelection();
  const gridViewportRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<WidgetFilter>("all");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingLabel, setEditingLabel] = useState<{ dataId: string; value: string } | null>(null);
  const [editingPage, setEditingPage] = useState<{ id: string; value: string } | null>(null);
  const [pendingDeletePage, setPendingDeletePage] = useState<WidgetPage | null>(null);
  const [gridViewportHeight, setGridViewportHeight] = useState(0);
  const [draggingPageId, setDraggingPageId] = useState<string | null>(null);
  const [pageDropIndicator, setPageDropIndicator] = useState<PageDropIndicator | null>(null);

  const currentColumns = normalizeGridValue(currentPage?.columns ?? DEFAULT_WIDGET_PAGE_COLUMNS, COLUMN_MIN, COLUMN_MAX);
  const currentRows = normalizeGridValue(currentPage?.rows ?? DEFAULT_WIDGET_PAGE_ROWS, ROW_MIN, ROW_MAX);
  const gridColumnSize = useMemo(() => getGridColumnSize(currentColumns), [currentColumns]);
  const widgetHeight = useMemo(() => {
    if (!gridViewportHeight) return 80;
    const totalGap = GRID_GAP * Math.max(0, currentRows - 1);
    const rowHeight = (gridViewportHeight - totalGap) / currentRows;
    return Math.max(MIN_WIDGET_CONTENT_HEIGHT, rowHeight - CARD_CHROME_HEIGHT);
  }, [currentRows, gridViewportHeight]);

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: "widget-area-drop",
    data: { area: "widget" },
  });

  const resolvedWidgets = useMemo(() => {
    return widgets
      .filter((w) => w.pageId === currentPageId)
      .map((w) => ({ widget: w, data: getDataById(w.dataId) }))
      .filter((r) => r.data !== undefined)
      .filter((r) => {
        if (filter !== "all") {
          const d = r.data!;
          if (filter === "html") {
            return d.mimeType === "text/html" || (d.type === "text" && isHtml(d.content));
          }
          if (filter === "markdown") {
            return d.type === "text" && d.mimeType !== "text/html" && isMarkdown(d.content);
          }
          if (filter === "text") {
            // "Sentence" filter: only plain text (not HTML, not Markdown, not URL)
            return d.type === "text" && d.mimeType !== "text/html" && !isHtml(d.content) && !isMarkdown(d.content) && !isUrl(d.content);
          }
          if (filter === "url") {
            return d.type === "url" || (d.type === "text" && isUrl(d.content));
          }
          if (r.data!.type !== filter) return false;
        }

        if (search.trim()) {
          const q = search.toLowerCase();
          return (
            r.data!.label.toLowerCase().includes(q) ||
            (r.data!.type === "text" && r.data!.content.toLowerCase().includes(q))
          );
        }
        return true;
      });
  }, [widgets, currentPageId, getDataById, search, filter]);

  useEffect(() => {
    const element = gridViewportRef.current;
    if (!element) return;

    const updateHeight = () => {
      setGridViewportHeight(element.clientHeight);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => window.removeEventListener("resize", updateHeight);
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [loading, resolvedWidgets.length]);

  const handleGridValueChange = useCallback(async (field: GridField, value: number) => {
    if (!currentPage) return;

    const min = field === "columns" ? COLUMN_MIN : ROW_MIN;
    const max = field === "columns" ? COLUMN_MAX : ROW_MAX;
    const currentValue = normalizeGridValue(currentPage[field], min, max);
    const nextValue = normalizeGridValue(value, min, max);
    if (nextValue === currentValue) return;

    await updateWidgetPage({ ...currentPage, [field]: nextValue });
    emitDataChanged();
  }, [currentPage]);

  const handleGridStep = useCallback((field: GridField, delta: number) => {
    if (!currentPage) return;

    void handleGridValueChange(field, currentPage[field] + delta);
  }, [currentPage, handleGridValueChange]);

  const handleAddPage = useCallback(async () => {
    const now = Date.now();
    const nextOrder = widgetPages.length > 0
      ? Math.max(...widgetPages.map((page) => page.order)) + 1
      : 0;
    const pageNumber = widgetPages.length + 1;
    const newPage = {
      id: crypto.randomUUID(),
      name: `${t("widget.page")} ${pageNumber}`,
      order: nextOrder,
      columns: DEFAULT_WIDGET_PAGE_COLUMNS,
      rows: DEFAULT_WIDGET_PAGE_ROWS,
      createdAt: now,
      updatedAt: now,
    };

    await addWidgetPage(newPage);
    emitDataChanged();
    setCurrentPageId(newPage.id);
    toast.success(t("widget.page_added", { name: newPage.name }));
  }, [setCurrentPageId, t, widgetPages]);

  const handleStartPageRename = useCallback((page: WidgetPage) => {
    setCurrentPageId(page.id);
    setEditingPage({ id: page.id, value: page.name });
  }, [setCurrentPageId]);

  const handleSavePageRename = useCallback(async () => {
    if (!editingPage) return;

    const page = widgetPages.find((candidate) => candidate.id === editingPage.id);
    const nextName = editingPage.value.trim();
    if (!page) {
      setEditingPage(null);
      return;
    }
    if (!nextName) {
      toast.error(t("widget.page_name_required"));
      return;
    }
    if (nextName === page.name) {
      setEditingPage(null);
      return;
    }

    await updateWidgetPage({ ...page, name: nextName });
    emitDataChanged();
    setEditingPage(null);
    toast.success(t("widget.page_renamed", { name: nextName }));
  }, [editingPage, t, widgetPages]);

  const handleCancelPageRename = useCallback(() => {
    setEditingPage(null);
  }, []);

  const handleRequestPageDelete = useCallback((page: WidgetPage) => {
    if (widgetPages.length <= 1) {
      toast.error(t("widget.page_delete_last_blocked"));
      return;
    }

    setPendingDeletePage(page);
  }, [t, widgetPages.length]);

  const handleConfirmPageDelete = useCallback(async () => {
    if (!pendingDeletePage || widgetPages.length <= 1) return;

    const fallbackPage = widgetPages.find((page) => page.id !== pendingDeletePage.id);
    if (currentPageId === pendingDeletePage.id && fallbackPage) {
      setCurrentPageId(fallbackPage.id);
    }

    const deletedWidgetCount = await deleteWidgetPageWithWidgets(pendingDeletePage.id);
    globalSelection.clearArea("widget");
    setPendingDeletePage(null);
    toast.success(t("widget.page_deleted", {
      name: pendingDeletePage.name,
      count: String(deletedWidgetCount),
    }));
  }, [currentPageId, globalSelection, pendingDeletePage, setCurrentPageId, t, widgetPages]);

  const persistPageOrder = useCallback(async (reorderedPages: WidgetPage[]) => {
    const updates = reorderedPages
      .map((page, order) => (page.order === order ? null : updateWidgetPage({ ...page, order })))
      .filter((update): update is Promise<void> => update !== null);

    if (updates.length === 0) return;

    await Promise.all(updates);
    emitDataChanged();
    toast.success(t("widget.pages_reordered"));
  }, [t]);

  const handlePageDragStart = useCallback((pageId: string, event: React.DragEvent<HTMLButtonElement>) => {
    if (widgetPages.length <= 1) return;

    setDraggingPageId(pageId);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", pageId);
  }, [widgetPages.length]);

  const handlePageDragOver = useCallback((targetPageId: string, event: React.DragEvent<HTMLDivElement>) => {
    if (!draggingPageId || draggingPageId === targetPageId) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const position = getPageDropPosition(event);
    setPageDropIndicator((current) => (
      current?.type === "page" && current.pageId === targetPageId && current.position === position
        ? current
        : { type: "page", pageId: targetPageId, position }
    ));
  }, [draggingPageId]);

  const handlePageEdgeDragOver = useCallback((position: PageEdgeDropPosition, event: React.DragEvent<HTMLDivElement>) => {
    if (!draggingPageId) return;
    if (position === "start" && widgetPages[0]?.id === draggingPageId) return;
    if (position === "end" && widgetPages[widgetPages.length - 1]?.id === draggingPageId) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setPageDropIndicator((current) => (
      current?.type === "edge" && current.position === position
        ? current
        : { type: "edge", position }
    ));
  }, [draggingPageId, widgetPages]);

  const handlePageDrop = useCallback(async (targetPageId: string, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const draggedPageId = event.dataTransfer.getData("text/plain") || draggingPageId;
    const position = pageDropIndicator?.type === "page" && pageDropIndicator.pageId === targetPageId
      ? pageDropIndicator.position
      : getPageDropPosition(event);

    setDraggingPageId(null);
    setPageDropIndicator(null);

    if (!draggedPageId || draggedPageId === targetPageId) return;

    const reorderedPages = reorderWidgetPages(widgetPages, draggedPageId, targetPageId, position);
    if (reorderedPages === widgetPages) return;

    await persistPageOrder(reorderedPages);
  }, [draggingPageId, pageDropIndicator, persistPageOrder, widgetPages]);

  const handlePageEdgeDrop = useCallback(async (position: PageEdgeDropPosition, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const draggedPageId = event.dataTransfer.getData("text/plain") || draggingPageId;

    setDraggingPageId(null);
    setPageDropIndicator(null);

    if (!draggedPageId) return;

    const reorderedPages = reorderWidgetPagesToEdge(widgetPages, draggedPageId, position);
    if (reorderedPages === widgetPages) return;

    await persistPageOrder(reorderedPages);
  }, [draggingPageId, persistPageOrder, widgetPages]);

  const handlePageDragEnd = useCallback(() => {
    setDraggingPageId(null);
    setPageDropIndicator(null);
  }, []);

  const handleCardClick = useCallback(async (widgetId: string, dataId: string, e: React.MouseEvent) => {
    const isMulti = e.metaKey || e.ctrlKey;
    const data = getDataById(dataId);
    if (!data) return;

    if (isMulti) {
      const isSelected = globalSelection.entries.some(en => en.itemId === widgetId);
      if (isSelected) {
        globalSelection.removeEntries([widgetId]);
      } else {
        globalSelection.addEntries([{ area: 'widget', itemId: widgetId, dataId }]);
      }
    } else {
      globalSelection.clearAll();
      globalSelection.addEntries([{ area: 'widget', itemId: widgetId, dataId }]);
    }

    const isRich =
      data.mimeType === "text/html" ||
      (data.type === "text" && (isHtml(data.content) || isMarkdown(data.content) || isJson(data.content) || isUrl(data.content))) ||
      data.type === "url";
    
    const clipType: ClipboardPreview["type"] = data.type === "image" ? "image" : 
                    (data.type === "url" || (data.type === "text" && isUrl(data.content))) ? "url" :
                    isRich ? (isHtml(data.content) ? "html" : isMarkdown(data.content) ? "markdown" : "json") : "text";

    emitPreviewRequest({
      id: data.id,
      type: clipType,
      content: data.content,
    });

    const ok = await copyToClipboard(data.content, (clipType === "html" || clipType === "markdown") ? clipType : clipType);
    if (ok) toast.success(t("menu.copy") + " ✓");

    const historyEntry: HistoryItem = {
      id: crypto.randomUUID(),
      dataId: data.id,
      createdAt: Date.now(),
    };
    await addHistoryItem(historyEntry);
    emitDataChanged();
  }, [getDataById, copyToClipboard, t, globalSelection]);

  const handleRightClickSelect = useCallback((itemId: string, dataId: string) => {
    const isSelected = globalSelection.entries.some(en => en.itemId === itemId);
    if (!isSelected) {
      globalSelection.clearAll();
      globalSelection.addEntries([{ area: 'widget', itemId, dataId }]);
    }
  }, [globalSelection]);

  const handleDeleteSelected = useCallback(async () => {
    const widgetEntries = globalSelection.entries.filter(e => e.area === 'widget');
    if (widgetEntries.length === 0) return;
    await deleteSelectedItems(widgetEntries);
    globalSelection.clearArea('widget');
    setShowDeleteDialog(false);
  }, [globalSelection]);

  const handleLabelSave = async () => {
    if (!editingLabel) return;
    const data = getDataById(editingLabel.dataId);
    if (!data) return;
    try {
      await updateDataItem({ ...data, label: editingLabel.value });
      emitDataChanged();
    } catch (e) {
      console.error("Label update failed:", e);
    }
    setEditingLabel(null);
  };

  const filterButtons: { type: WidgetFilter; icon: typeof FileText; label: string }[] = [
    { type: "text", icon: FileText, label: t("widget.text") },
    { type: "url", icon: Globe, label: "Link" },
    { type: "image", icon: Image, label: t("widget.image") },
    { type: "html", icon: CodeXml, label: t("widget.html") },
    { type: "markdown", icon: FileType, label: t("widget.markdown") },
  ];

  const selectedWidgetIds = new Set(
    globalSelection.entries.filter(e => e.area === 'widget').map(e => e.itemId)
  );

  return (
    <>
      <motion.section
        ref={setDropRef}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        className={`glass-panel-static rounded-2xl p-4 flex flex-col h-full min-h-0 overflow-hidden transition-colors ${
          isOver ? "ring-2 ring-inset ring-primary/50 bg-primary/5" : ""
        }`}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("widget.search")}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg bg-background/40 border border-border/50 text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="flex gap-1.5 shrink-0">
            {filterButtons.map(({ type, icon: Icon, label }) => (
              <button
                key={type}
                onClick={() => setFilter((f) => (f === type ? "all" : type))}
                className={`rounded-md px-2 py-1 text-xs flex items-center gap-1 transition-all border ${
                  filter === type
                    ? "bg-primary/20 text-primary border-primary/60 font-medium"
                    : "glass-bar text-muted-foreground border-border/30 hover:text-foreground hover:border-border/60"
                }`}
              >
                <Icon className="w-3 h-3" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </div>
        ) : resolvedWidgets.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-muted-foreground/60">
              <Save className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-xs whitespace-pre-line">{t("widget.empty")}</p>
            </div>
          </div>
        ) : (
          <ScrollArea viewportRef={gridViewportRef} className="flex-1 min-h-0 h-0 overflow-x-hidden">
            <div
              data-testid="widget-grid"
              className="gap-2 pr-2 pb-1 content-start"
              style={{
                display: 'grid',
                gap: `${GRID_GAP}px`,
                gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${gridColumnSize}), ${gridColumnSize}))`,
                justifyContent: 'start',
              }}
            >
              <AnimatePresence mode="popLayout">
                {resolvedWidgets.map(({ widget, data }) => (
                  <WidgetCard
                    key={widget.id}
                    widget={widget}
                    data={data!}
                    widgetHeight={widgetHeight}
                    selectedWidgetIds={selectedWidgetIds}
                    editingLabel={editingLabel}
                    setEditingLabel={setEditingLabel}
                    handleLabelSave={handleLabelSave}
                    handleCardClick={handleCardClick}
                    handleRightClickSelect={handleRightClickSelect}
                    setShowDeleteDialog={setShowDeleteDialog}
                    globalSelection={globalSelection}
                  />
                ))}
              </AnimatePresence>
            </div>
          </ScrollArea>
        )}

        <div className="shrink-0 mt-2 flex items-center gap-2 border-t border-border/30 pt-2">
          <div className="flex shrink-0 items-center gap-1.5">
            <GridStepper
              label={t("widget.columns")}
              value={currentColumns}
              min={COLUMN_MIN}
              max={COLUMN_MAX}
              testId="widget-columns"
              disabled={!currentPage}
              onStep={(delta) => void handleGridStep("columns", delta)}
              onCommit={(value) => void handleGridValueChange("columns", value)}
            />
            <GridStepper
              label={t("widget.rows")}
              value={currentRows}
              min={ROW_MIN}
              max={ROW_MAX}
              testId="widget-rows"
              disabled={!currentPage}
              onStep={(delta) => void handleGridStep("rows", delta)}
              onCommit={(value) => void handleGridValueChange("rows", value)}
            />
          </div>

          <div className="h-5 w-px shrink-0 bg-border/30" />

          <div className="flex min-w-0 flex-1 items-center gap-1">
            <LayoutGrid className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1 overflow-x-auto">
              <div className="flex w-max min-w-full items-center gap-1 pb-0.5">
                <div
                  data-testid="widget-page-edge-start"
                  onDragOver={(event) => handlePageEdgeDragOver("start", event)}
                  onDrop={(event) => void handlePageEdgeDrop("start", event)}
                  className="relative h-7 w-3 shrink-0"
                >
                  {pageDropIndicator?.type === "edge" && pageDropIndicator.position === "start" && (
                    <span className="absolute right-0 top-1 bottom-1 w-0.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary)/0.8)]" />
                  )}
                </div>
                {widgetPages.map((page) => {
                  const isCurrent = page.id === currentPageId;
                  const isEditing = editingPage?.id === page.id;

                  if (isEditing) {
                    return (
                      <div
                        key={page.id}
                        className="flex shrink-0 items-center gap-1 rounded-md border border-primary/60 bg-primary/10 px-1 py-0.5"
                      >
                        <input
                          data-testid="widget-page-name-input"
                          value={editingPage.value}
                          onChange={(event) => setEditingPage({ id: page.id, value: event.target.value })}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") void handleSavePageRename();
                            if (event.key === "Escape") handleCancelPageRename();
                          }}
                          autoFocus
                          className="h-6 w-24 rounded bg-background/70 px-1.5 text-[11px] text-foreground outline-none ring-1 ring-primary/30"
                        />
                        <button
                          type="button"
                          aria-label={t("widget.save_page_name")}
                          data-testid="widget-page-name-save"
                          title={t("widget.save_page_name")}
                          onClick={() => void handleSavePageRename()}
                          className="flex h-6 w-6 items-center justify-center rounded text-primary transition-colors hover:bg-primary/15"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          aria-label={t("widget.cancel_page_rename")}
                          data-testid="widget-page-name-cancel"
                          title={t("widget.cancel_page_rename")}
                          onClick={handleCancelPageRename}
                          className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={page.id}
                      data-testid={`widget-page-pill-${page.id}`}
                      onDragOver={(event) => handlePageDragOver(page.id, event)}
                      onDrop={(event) => void handlePageDrop(page.id, event)}
                      className={`group relative flex shrink-0 items-center rounded-md border transition-colors ${
                        isCurrent
                          ? "border-primary/60 bg-primary/15 text-primary"
                          : "border-border/40 bg-background/30 text-muted-foreground hover:text-foreground"
                      } ${draggingPageId === page.id ? "opacity-50" : ""}`}
                    >
                      {pageDropIndicator?.type === "page" && pageDropIndicator.pageId === page.id && pageDropIndicator.position === "before" && (
                        <span className="absolute -left-1 top-1 bottom-1 w-0.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary)/0.8)]" />
                      )}
                      <button
                        type="button"
                        data-testid={`widget-page-tab-${page.id}`}
                        title={page.name}
                        draggable={widgetPages.length > 1}
                        onDragStart={(event) => handlePageDragStart(page.id, event)}
                        onDragEnd={handlePageDragEnd}
                        onClick={() => setCurrentPageId(page.id)}
                        className={`max-w-28 truncate px-2 py-1 text-[11px] ${
                          widgetPages.length > 1 ? "cursor-grab active:cursor-grabbing" : ""
                        }`}
                      >
                        {page.name}
                      </button>
                      {pageDropIndicator?.type === "page" && pageDropIndicator.pageId === page.id && pageDropIndicator.position === "after" && (
                        <span className="absolute -right-1 top-1 bottom-1 w-0.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary)/0.8)]" />
                      )}
                      {isCurrent && (
                        <div className="flex items-center pr-0.5">
                          <button
                            type="button"
                            aria-label={t("widget.rename_page")}
                            draggable={false}
                            data-testid={`widget-page-rename-${page.id}`}
                            title={t("widget.rename_page")}
                            onClick={() => handleStartPageRename(page)}
                            className="flex h-6 w-6 items-center justify-center rounded text-primary/80 transition-colors hover:bg-primary/15 hover:text-primary"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            aria-label={t("widget.delete_page")}
                            draggable={false}
                            data-testid={`widget-page-delete-${page.id}`}
                            title={t("widget.delete_page")}
                            onClick={() => handleRequestPageDelete(page)}
                            className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                <div
                  data-testid="widget-page-edge-end"
                  onDragOver={(event) => handlePageEdgeDragOver("end", event)}
                  onDrop={(event) => void handlePageEdgeDrop("end", event)}
                  className="relative h-7 min-w-3 flex-1"
                >
                  {pageDropIndicator?.type === "edge" && pageDropIndicator.position === "end" && (
                    <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary)/0.8)]" />
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              aria-label={t("widget.add_page")}
              data-testid="widget-page-add"
              title={t("widget.add_page")}
              onClick={() => void handleAddPage()}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border/40 bg-background/30 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </motion.section>

      <DeleteConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteSelected}
        count={globalSelection.entries.filter(e => e.area === 'widget').length}
      />
      <ConfirmDialog
        open={pendingDeletePage !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeletePage(null);
        }}
        title={t("widget.delete_page")}
        description={t("widget.delete_page_confirm", { name: pendingDeletePage?.name ?? "" })}
        onConfirm={() => void handleConfirmPageDelete()}
        variant="destructive"
      />
    </>
  );
};

export default WidgetArea;
